import { PreactRenderer, Shark, SwimWay } from "shark-mvc";
import { NousCore } from "nouscore-js-api";

import ActiveItems from "./models/ActiveItems";
import OrganizationMembership from "./models/OrganizationMembership";
import User from "./models/User";

import acceptInvitationView from "./views/acceptInvitation/acceptInvitationView";
import homeView from "./views/home/homeView";
import localizationManager from "./managers/localizationManager";
import notificationManager from "./managers/notificationManager";
import signView from "./views/sign/signView";
import systemComponent from "./components/systemComponent";
import workSessionManager from "./managers/workSessionManager";

import "bootstrap";
import "moment-duration-format";
import "jquery";
import "popper.js";

import "bootstrap/dist/css/bootstrap.min.css";
import "./css/custom.css";
import passwordResetView from "./views/password-recovery/passwordResetView";

function needSignIn () {
	Shark.openPage(signView);
}

async function startUp () {
	NousCore.api.settings.commonEndPointBase = process.env.API_URL;//"https://www.timecorder.net/api/";
	// NousCore.api.settings.commonEndPointBase = "https://app.timecorder.net/api/v1/";

	// Shark.traceLevel = Shark.TRACE_LOG;
	Shark.traceLevel = Shark.TRACE_NONE;
	Shark.init({
		enableJawStoreAutoRender: true,
		enableMainStoreAutoRender: true,
		enableSmartListener2: true,
		enableSmartModelSetUpdatesView: true,
		routingMethod: "url",
		templateRenderer: new PreactRenderer(),
	});

	localizationManager.init();

	const applicationSettingsData = localStorage.getItem("applicationSettings") || "{}";
	const applicationSettings = JSON.parse(applicationSettingsData);

	applicationSettings.newProjectClientGuid = "";
	applicationSettings.newProjectOrganizationGuid = "";

	const router = new SwimWay(Shark.settings.routingMethod);

	window.addEventListener("load", () => {
		if ("serviceWorker" in navigator) {
			navigator.serviceWorker.addEventListener("message", event => {
				// console.log("Got reply from service worker: ", event.data);

				if (event.data.name === "cacheVersion") {
					Shark.set({
						cacheRevisionVersion: event.data.value,
					});
					// Shark.render();
				}
			});

			navigator.serviceWorker.register("/main-sw.js")
			.then(function () {
				// console.log("ServiceWorker Registered", this);
			}, function (err) {
				// registration failed :(
				// console.log("ServiceWorker registration failed: ", err);
			}).catch(function (err) {
				// console.log("Service Worker Failed to Register", err);
			});
		}
	});

	window.addEventListener("focus", async () => {
		const remoteAppVersionResponse = await systemComponent.checkAppVersion();
		const remoteAppVersion = remoteAppVersionResponse.version;
		const localAppVersion = Shark.$store.appVersion;

		// console.log({remoteAppVersion, localAppVersion});

		if (localAppVersion !== remoteAppVersion) {
			if (process.env.NODE_ENV !== "development") {
				document.location.reload();
				return;
			}
		}

		// Dave.ToDo: Questa cosa la fa anche su view in cui non si è loggati
		await systemComponent.checkOpenWorkSession();
		// Shark.render();
	});

	//ALICE: dovrebbe esistere anche una role map?
	const userRoleMap = [
		{
			icon: "icon-owner",
			id: OrganizationMembership.ROLE_OWNER,
			label: "Owner",
		},
		{
			icon: "icon-powerAdmin",
			id: OrganizationMembership.ROLE_POWER_ADMIN,
			label: "Power Admin",
		},
		{
			icon: "icon-admin",
			id: OrganizationMembership.ROLE_ADMIN,
			label: "Admin",
		},
		{
			icon: "icon-powerUser",
			id: OrganizationMembership.ROLE_POWER_USER,
			label: "Power User",
		},
		{
			icon: "icon-user",
			id: OrganizationMembership.ROLE_USER,
			label: "User",
		},
	];

	const projectStatusMap = [
		{
			icon: "icon-progress",
			id: 1,
			label: "In corso",
		},
		{
			icon: "icon-pending",
			id: 3,
			label: "In pausa",
		},
		{
			icon: "icon-done",
			id: 7,
			label: "Chiuso",
		},
	];

	const taskStatusMap = [
		{
			icon: "icon-progress",
			id: 1,
			label: "In corso",
		},
		{
			icon: "icon-progress",
			id: 2,
			label: "In pausa",
		},
		{
			icon: "icon-done",
			id: 7,
			label: "Chiuso",
		},
	];

	Shark.set({
		activeItems: new ActiveItems(applicationSettings),
		appBuildDate: __BUILD_DATE__,
		appVersion: __VERSION__,
		cdnURL: process.env.CDN_URL,//"https://www.timecorder.net",
		// cdnURL: "https://app.timecorder.net",
		isMobile: "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0,
		moneyFormatter: new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }),
		numberFormatter: new Intl.NumberFormat("it-IT", { maximumFractionDigits: 2 }), 
		projectStatusMap,
		router,
		taskStatusMap,
		userRoleMap,
		userRoleAssignableMap: userRoleMap.filter(item => item.id != OrganizationMembership.ROLE_OWNER),
	});

	const urlParams = new URLSearchParams(document.location.search);

	if (urlParams.has("k")) {
		const currUser = new User();
		const tempKey = urlParams.get("k") || "";
		const guid = urlParams.get("g") || "";

		currUser.set({
			guid,
			tempKey,
		});
		
		Shark.set({
			confirmSent: false,
			currUser,
			tempKey,
			__tempKeyIsValid: tempKey !== null,
		});

		Shark.openPage(passwordResetView, { urlSearchParams: null });
	}
	else {
		const requestedView = Shark.$store.router.getRequestedViewFromLocation();
								
		if (requestedView === "accept-invitation") {
			Shark.openPage(acceptInvitationView);
		}
		else {
			if (localStorage.getItem("preserveLogin") === "true") {
				const validateSessionResponse = await NousCore.api.validateSession();

				if (validateSessionResponse.sessionIsValid) {
					const getSessionUserResponse = await NousCore.api.getSessionUser();

					if (getSessionUserResponse.userData) {
						const currUser = new User(getSessionUserResponse.userData);
						// ALICE: Theme
						document.body.classList.add(currUser.preferences.theme);
						Shark.set({currUser});
						
						await systemComponent.prepareSystemData();
						await workSessionManager.checkOpenByUser(currUser);

						if (!Shark.get("runningWorkSession") || Shark.get("runningWorkSession").guid === "") {
							notificationManager.startShouldBeWorkingNotifications();
						}
					
						let requestedView = Shark.$store.router.getRequestedViewFromLocation();
						if (Shark.$store.__firstOrganizationToAdd)
						{
							requestedView = "organizationView";
						}
						// console.log({ __firstOrganizationToAdd: Shark.$store.__firstOrganizationToAdd });
						
						if (requestedView === "") {
							Shark.openPage(homeView);
						}
						else {
							Shark.openPage(requestedView);
						}
					}
					else {
						needSignIn();
					}
				}
				else {
					needSignIn();
				}
			}
			else {
				needSignIn();
			}
		}
	}
}

startUp();